<template>
  <div class="py-4">
    <div v-if="title" class="mb-2.5 flex items-baseline">
      <p
        :class="cn('ui-text-m mr-1.5 font-normal capitalize', labelClass)"
        :data-e2e-item="`${block.sku}-filter-group-${block.view_type}`"
      >
        {{ title }}:
      </p>
    </div>
    <div>
      <UiScrollableSlider
        ref="slider"
        slides-count="auto"
        :items-list="block.materials"
        class="-mx-4 lg:hidden"
        :gap-x="16"
      >
        <template #card="{ item }">
          <button
            class="group flex w-[94px] flex-col items-center gap-2.5 border pb-2.5 pt-5 text-xxs font-light uppercase disabled:border-gray-10 disabled:bg-gray-5 disabled:text-gray-30"
            :class="
              cn(
                [
                  selectedItem?.id === item.id
                    ? 'border-red-80'
                    : 'border-gray-10',
                ],
                {
                  'border-gray-10 bg-gray-5 text-gray-30':
                    item.disabled && disabledButClickable,
                },
              )
            "
            :disabled="!skipDisable && item.disabled && !disabledButClickable"
            :data-e2e-item="`${item.sku}-filter-${block.view_type}`"
            @click="onFilterClick(block.slug, item)"
          >
            <UiLazyImg
              class="mb-2.5 aspect-1 size-15 overflow-hidden object-contain group-disabled:opacity-50"
              :class="[
                block.slug === 'diamond-shape'
                  ? 'transition-all md:group-hover:scale-[1.1]'
                  : '',
              ]"
              :src="
                getImageByPosition(item.medias, 'list')?.file?.url ||
                '/images/no-image.webp'
              "
              :alt="
                getImageByPosition(item.medias, 'list')?.file?.alt ||
                getFilterImageAlt(item, block)
              "
            />
            <span class="break-word-custom px-0.5">
              {{ item?.currentTranslation?.title }}
            </span>
          </button>
        </template>
      </UiScrollableSlider>
      <div class="relative max-lg:hidden">
        <div
          class="absolute inset-y-0 left-0 z-10 flex w-[94px] items-center justify-center bg-gradient-to-l from-white/50 from-[41%] to-white px-4 has-[button.hidden]:hidden"
          :class="swiperCarousel ? 'flex' : 'hidden'"
        >
          <button
            ref="prevEl"
            :aria-label="$t('previousSlide')"
            class="inline-flex items-center justify-center rounded-full border border-gray-70 bg-off-white-10 p-2.5 transition-all"
          >
            <UiIcon name="arrow_left" class="size-6 text-primary" />
          </button>
        </div>

        <Swiper
          :slides-per-view="6"
          :space-between="16"
          :modules="modules"
          round-lengths
          :navigation="navigation"
          :breakpoints="{
            [screens.lg]: {
              spaceBetween: 16,
              slidesPerView: 4,
            },
            [screens.xl]: {
              spaceBetween: 16,
              slidesPerView: isRegularPdp ? 4 : 6,
            },
          }"
          class="w-full"
          @swiper="onSwiperInit"
        >
          <SwiperSlide v-for="item in block.materials" :key="item.id">
            <button
              class="group flex w-[94px] flex-col items-center gap-2.5 border pb-2.5 pt-5 text-xxs font-light uppercase disabled:border-gray-10 disabled:bg-gray-5 disabled:text-gray-30"
              :class="
                cn([
                  selectedItem?.id === item.id
                    ? 'border-red-80'
                    : 'border-gray-10',
                  {
                    'border-gray-10 bg-gray-5 text-gray-30':
                      item.disabled && disabledButClickable,
                  },
                ])
              "
              :disabled="!skipDisable && item.disabled && !disabledButClickable"
              @click="onFilterClick(block.slug, item)"
            >
              <UiLazyImg
                class="mb-2.5 aspect-1 size-15 overflow-hidden object-contain group-disabled:opacity-50"
                :class="[
                  block.slug === 'diamond-shape'
                    ? 'transition-all md:group-hover:scale-[1.1]'
                    : '',
                ]"
                :src="
                  getImageByPosition(item.medias, 'list')?.file?.url ||
                  '/images/no-image.webp'
                "
                :alt="
                  getImageByPosition(item.medias, 'list')?.file?.alt ||
                  getFilterImageAlt(item, block)
                "
              />
              <span class="break-word-custom px-0.5">
                {{ item?.currentTranslation?.title }}
              </span>
            </button>
          </SwiperSlide>
        </Swiper>
        <div
          class="absolute inset-y-0 right-0 z-10 flex w-[94px] items-center justify-center bg-gradient-to-r from-white/50 from-[41%] to-white px-4 has-[button.hidden]:hidden"
          :class="swiperCarousel ? 'flex' : 'hidden'"
        >
          <button
            ref="nextEl"
            :aria-label="$t('nextSlide')"
            class="inline-flex items-center justify-center rounded-full border border-gray-70 bg-off-white-10 p-2.5 transition-all"
          >
            <UiIcon name="arrow_right" class="size-6 text-primary" />
          </button>
        </div>
      </div>
    </div>
    <SharedFieldHint :message="errorMessage" error />
    <slot name="additional" />
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Swiper as SwiperInstance } from "swiper";
import { NavigationOptions } from "swiper/types";
import {
  IProductMaterialFilterEvent,
  IProductMaterialFilterProps,
  MaterialData,
} from "ecom-types";
import { screens } from "@/configs/breakpointsConfig.json";

const props = defineProps<
  IProductMaterialFilterProps & {
    isRegularPdp?: boolean;
    skipDisable?: boolean;
    disabledButClickable?: boolean;
  }
>();

const slider = ref();

const emit = defineEmits<{
  (e: "filter", value: IProductMaterialFilterEvent): void;
}>();

const { navigation, nextEl, prevEl, modules, onSwiperInit, swiperCarousel } =
  useSwiperCarousel();

const { errorMessage, setValue } = useField(props.block?.slug, undefined, {
  initialValue: props.selectedItem?.sku,
});

watch(
  () => props.selectedItem,
  () => {
    setValue(props.selectedItem?.sku || "");
  },
  { deep: true },
);

function onFilterClick(
  slug: string,
  item: MaterialData & { disabled?: boolean },
) {
  setValue(item.sku);
  emit("filter", { slug, item });
  if (item.disabled && !props.disabledButClickable) {
    const foundIndex = props.block.materials.findIndex(
      (material) => material.id === props.selectedItem?.id,
    );
    const clampedIndex = Math.max(
      0,
      Math.min(props.block.materials.length - 1, foundIndex),
    );
    swiperCarousel.value.slideTo(
      clampedIndex > 0
        ? Math.min(props.block.materials.length - 1, clampedIndex + 1)
        : clampedIndex,
    );

    if (slider.value) {
      slider.value.scrollTo(clampedIndex);
    }
  }
}

function getFilterImageAlt(option: MaterialData, category: any) {
  let res = "";
  if (category?.currentTranslation?.title) {
    res = res + category.currentTranslation.title;
  }
  if (option?.currentTranslation?.title) {
    res = res + " " + option.currentTranslation.title;
  }
  return res;
}

function useSwiperCarousel() {
  const modules = [Navigation];
  const prevEl = ref<HTMLElement | null>(null);
  const nextEl = ref<HTMLElement | null>(null);

  const swiperCarousel = ref<typeof SwiperInstance>();

  const navigation = reactive({
    prevEl,
    nextEl,
    disabledClass: "hidden",
  }) as NavigationOptions;

  function onSwiperInit(swiper: typeof SwiperInstance) {
    swiperCarousel.value = swiper;
  }

  return {
    onSwiperInit,
    swiperCarousel,
    navigation,
    prevEl,
    nextEl,
    modules,
  };
}
</script>
